import React from 'react';

import { isNShowcaseLocal, NEWTON_LOGO_LIGHT_SRC, NEWTON_LOGO_SRC } from '@/utils/nShowcase';

import { DarkThemeLockup, LightThemeLockup, Lockup } from './AuthLogo.styles';

/**
 * The Whitelabels.com brand lockup for the full-bleed auth screens (`/login` and
 * `/register`), in whichever treatment the active theme needs.
 *
 * The artwork is an `<img>`-loaded SVG, so its fills are sealed inside the SVG
 * document: no CSS variable, no `--ows-*` token and none of the light-theme
 * derivation can reach them. The dark-theme lockup is near-white (#E4E4E7), which
 * on the light theme's pale wallpaper left the brand all but invisible. The fix is
 * the same one the configurator wizard uses: ship BOTH treatments and let CSS pick.
 *
 * Both files are the identical 779x128 artwork and differ only in their fills, so
 * the lockup never shifts or resizes when the theme flips.
 *
 * This is the single place either lockup's location is written down. Both auth
 * pages render this component, so changing an asset is a one-line edit here rather
 * than the two hardcoded, silently-diverging copies it replaced.
 *
 * With the Newton showcase on, both treatments show Newton's logo instead. The
 * browser-local switch (`localStorage.n_showcase`, see `utils/nShowcase`) is read
 * here, since most of these screens are signed out; a signed-in caller
 * (`/change-password`) passes the owner preference in through `newton`.
 */

/** Light-on-dark lockup (near-white #E4E4E7 fills), for the dark theme. */
const DARK_THEME_SRC = 'https://micros-t.b-cdn.net/wl-logo-1770387666913.svg';

/** Dark-on-light lockup (black fills), for the light theme. Same artwork, and the
 *  only copy of it in the repo, it is served from the configurator wizard's asset
 *  folder because that flow needed it first, not because it is wizard-specific. */
const LIGHT_THEME_SRC = '/assets/config-v3/logo-light.svg';

/** Decorative: the page's own heading carries the accessible name, and the lockup
 *  would only repeat the brand a screen reader already reads out of the URL. */
const DECORATIVE = '';

export type AuthLogoProps = {
  /** The Newton showcase is on for the signed-in owner (`selectNShowcase`). The
   *  local switch turns it on regardless. Default `false`. */
  newton?: boolean;
};

export const AuthLogo = ({ newton = false }: AuthLogoProps): React.ReactElement => {
  const showNewton = newton || isNShowcaseLocal();
  return (
    <Lockup className="logo">
      <DarkThemeLockup src={showNewton ? NEWTON_LOGO_SRC : DARK_THEME_SRC} alt={DECORATIVE} />
      <LightThemeLockup src={showNewton ? NEWTON_LOGO_LIGHT_SRC : LIGHT_THEME_SRC} alt={DECORATIVE} />
    </Lockup>
  );
};

export default AuthLogo;
